import { ApiError } from './api/client';

/**
 * The API's error keys in pt-BR. The keys stay machine-readable in the payload
 * (02-requirements.md §Admin Dashboard); this is the one place they become
 * something a person reads, so a message is never written twice.
 */
const MESSAGES: Record<string, string> = {
  admin_only: 'Sua conta não tem permissão para isso.',
  unauthorized: 'Sua sessão acabou. Entre novamente.',
  too_many_attempts: 'Muitas tentativas seguidas. Espere alguns minutos e tente de novo.',
  poll_not_found: 'Esta enquete não existe mais.',
  candidate_not_found: 'Esta opção não existe mais.',
  confirm_mismatch: 'O título digitado não confere.',
  candidate_has_votes: 'Esta opção já recebeu votos.',
  candidate_set_mismatch: 'A ordem enviada não bate com as opções da enquete.',
  media_in_use: 'Esta imagem está em uso por uma enquete ou opção.',
  media_bundled: 'Esta imagem vem com o servidor e não pode ser apagada.',
  media_not_found: 'Esta imagem não existe mais.',
  invalid_media_name: 'Nome de imagem inválido.',
  unsupported_media_type: 'Formato não aceito. Use PNG, JPEG ou WebP.',
  file_too_large: 'A imagem passa de 5 MB.',
  no_file: 'Nenhum arquivo foi enviado.',
  invalid_upload: 'O envio falhou. Tente de novo.',
  invalid_url: 'Endereço inválido.',
  blocked_address: 'Este endereço não pode ser buscado pelo servidor.',
  fetch_failed: 'Não foi possível baixar a imagem desse endereço.',
  network_error: 'Não foi possível falar com a API.',
  rights_required: 'Informe o direito de uso da imagem antes de enviá-la.',
  invalid_range: 'O fim do período precisa ser depois do início.',
  blackout_not_found: 'Este período não existe mais.',
  report_not_found: 'Esta denúncia não existe mais.',
  transport_unavailable: 'Este canal não está configurado no servidor.',
  transport_switch_failed: 'Não foi possível trocar o canal do WhatsApp.',
  whatsapp_worker_unreachable: 'O serviço de WhatsApp não respondeu.',
  already_linked: 'Este número já está conectado.',
  pairing_not_supported: 'Código de pareamento só existe no Baileys.',
  pairing_failed: 'Não foi possível gerar o código de pareamento.',
};

/** Everything the screens show for a failure. Unknown keys still say *something*. */
export function errorMessage(cause: unknown): string {
  if (cause instanceof ApiError) {
    return MESSAGES[cause.code] ?? `Falha na operação (${cause.code}).`;
  }
  if (cause instanceof Error) return MESSAGES.network_error as string;
  return 'Falha na operação.';
}

/** True when the failure is the API refusing a delete that still holds votes. */
export const isVotesConflict = (cause: unknown): boolean =>
  cause instanceof ApiError && cause.code === 'candidate_has_votes';

const MONTHS = [
  'janeiro',
  'fevereiro',
  'março',
  'abril',
  'maio',
  'junho',
  'julho',
  'agosto',
  'setembro',
  'outubro',
  'novembro',
  'dezembro',
];

/**
 * "desde janeiro de 2026" — month granularity, written by hand rather than
 * through `toLocaleString`, the same rule and the same reason as the app's
 * `lib/format.js`. An unusable date renders nothing, never "Invalid Date".
 */
export function sinceLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return `desde ${MONTHS[date.getMonth()]} de ${date.getFullYear()}`;
}

/** 1.234 — pt-BR grouping, same reason: Intl is not dependable across runtimes. */
export function ptNumber(value: number): string {
  const digits = Math.trunc(Math.abs(value)).toString();
  let out = '';
  for (let index = 0; index < digits.length; index += 1) {
    if (index > 0 && (digits.length - index) % 3 === 0) out += '.';
    out += digits[index];
  }
  return value < 0 ? `-${out}` : out;
}

/** "1,2 MB" / "840 kB" for the media library's size column. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}

/** "28/09/2026 18:00" in Brasília — the timezone the electoral calendar is set in. */
export function dateTimeLabel(iso: string | null | undefined): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - 3 * 60 * 60 * 1000); // UTC-3, no DST since 2019
  const two = (value: number) => String(value).padStart(2, '0');
  return (
    `${two(local.getUTCDate())}/${two(local.getUTCMonth() + 1)}/${local.getUTCFullYear()} ` +
    `${two(local.getUTCHours())}:${two(local.getUTCMinutes())}`
  );
}
